import React, { useEffect, useRef, useState } from 'react';
import { ImageFallback, Img } from '@/components/ui';

/**
 * The selected variant's images. Desktop: thumbnail strip + a main image that
 * zooms under the pointer. Phones: a swipeable, snap-scrolling strip with dots.
 * `resetKey` (the variant id) snaps back to the first image when the shopper
 * picks another colour, so they see the new colour straight away.
 */
const ProductGallery = ({ images = [], title, resetKey, children }) => {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(null);
  const stripRef = useRef(null);
  const list = images.length ? images : [null];

  useEffect(() => {
    setIndex(0);
    stripRef.current?.scrollTo({ left: 0 });
  }, [resetKey]);

  const onStripScroll = () => {
    const el = stripRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const current = list[Math.min(index, list.length - 1)];

  return (
    <div>
      {/* Phones */}
      <div className="relative md:hidden">
        <div
          ref={stripRef}
          onScroll={onStripScroll}
          className="scrollbar-hide flex snap-x snap-mandatory overflow-x-auto rounded-2xl bg-white"
        >
          {list.map((src, i) => (
            <div key={`${src}-${i}`} className="aspect-[4/5] w-full shrink-0 snap-center">
              <Img src={src} alt={`${title} — image ${i + 1}`} label={title} className="h-full w-full" />
            </div>
          ))}
        </div>
        {children}
        {list.length > 1 && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5" aria-hidden="true">
            {list.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-ink-900' : 'w-1.5 bg-ink-900/30'}`} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden gap-3 md:flex">
        {list.length > 1 && (
          <div className="scrollbar-hide flex max-h-[560px] w-[72px] shrink-0 flex-col gap-2 overflow-y-auto">
            {list.map((src, i) => (
              <button
                key={`${src}-${i}`}
                type="button"
                onMouseEnter={() => setIndex(i)}
                onClick={() => setIndex(i)}
                aria-label={`Show image ${i + 1}`}
                aria-current={i === index}
                className={`lk-focus aspect-[4/5] w-full shrink-0 overflow-hidden rounded-xl bg-white transition ${
                  i === index ? 'ring-2 ring-ink-900' : 'ring-1 ring-ink-200 hover:ring-ink-400'
                }`}
              >
                {src && <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />}
              </button>
            ))}
          </div>
        )}
        <div
          className="relative aspect-[4/5] max-h-[560px] flex-1 cursor-zoom-in overflow-hidden rounded-2xl bg-white ring-1 ring-ink-200/70"
          onMouseMove={onMove}
          onMouseLeave={() => setZoom(null)}
        >
          {current ? (
            <img
              key={current}
              src={current}
              alt={title}
              className="h-full w-full object-cover transition-transform duration-200 ease-out"
              style={zoom ? { transform: 'scale(2)', transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined}
            />
          ) : (
            <ImageFallback label={title} className="h-full w-full" />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default ProductGallery;
